/* eslint-disable */
// @ts-nocheck

import { FilterX, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { useCompanyNames } from '../hooks/useCompanyNames'

// Simplified interface for nuqs-based filters
interface SelectionSummaryProps {
  // Active nuqs filters
  activeStatuses: string[]
  setActiveStatuses: (_statuses: string[]) => void
  statusOptions: { value: string; label: string }[]
  searchQuery: string
  activeOrigins?: string[]
  setActiveOrigins?: (_origins: string[]) => void
  originsOptions?: { value: string; label: string }[]
  setSearchQuery: (_searchQuery: string) => void
  handleClearFilters: () => void

  // New filters
  activeCategories?: string[]
  setActiveCategories?: (_categories: string[]) => void
  categoryOptions?: { value: string; label: string }[]
  activeIsReturned?: string
  setActiveIsReturned?: (_isReturned: string) => void
  returnedOptions?: { value: string; label: string }[]

  // Company filters
  ownerCompanyId?: string
  logisticCompanyId?: string
  transporterCompanyId?: string
  storageCompanyId?: string
  updateOwnerCompanyId?: (_id: string) => void
  updateLogisticCompanyId?: (_id: string) => void
  updateTransporterCompanyId?: (_id: string) => void
  updateStorageCompanyId?: (_id: string) => void
}

// Legacy interface - commented out for now
// interface LegacySelectionSummaryProps {
//   activeStatuses: string[]
//   setActiveStatuses: (statuses: string[]) => void
//   statusOptions: { value: string; label: string }[]
//   selectedTipo: string
//   setSelectedTipo: (tipo: string) => void
//   personFilters: { type: string; name: string; label: string }[]
//   handleRemovePersonFilter: (index: number) => void
//   handleClearFilters: () => void
//   tipoOptions: { value: string; label: string }[]
//   selectedProvincia: string
//   setSelectedProvincia: (provincia: string) => void
//   selectedMunicipio: string
//   setSelectedMunicipio: (municipio: string) => void
//   lugarLabel: string
//   searchQuery: string
//   dateRange: DateRange
//   setDateRange: (dateRange: DateRange) => void
//   dateTypeOptions: { value: string; label: string }[]
//   selectedDateType: string
//   amountRange: { min: string; max: string }
//   setAmountRange: (amountRange: { min: string; max: string }) => void
//   selectedFilterCriteria: string[]
//   setSelectedFilterCriteria: (criteria: string[]) => void
//   activeFilters: { name: string; value: string }[]
//   setActiveFilters: (filters: { name: string; value: string }[]) => void
//   setSearchQuery: (searchQuery: string) => void
// }

export const SelectionSummary = ({
  activeStatuses,
  setActiveStatuses,
  statusOptions,
  searchQuery,
  setSearchQuery,
  handleClearFilters,
  activeOrigins,
  setActiveOrigins: _setActiveOrigins,
  originsOptions: _originsOptions,
  // New filters
  activeCategories,
  setActiveCategories,
  categoryOptions,
  activeIsReturned,
  setActiveIsReturned,
  returnedOptions,
  // Company filters
  ownerCompanyId,
  logisticCompanyId,
  transporterCompanyId,
  storageCompanyId,
  updateOwnerCompanyId,
  updateLogisticCompanyId,
  updateTransporterCompanyId,
  updateStorageCompanyId,
}: SelectionSummaryProps) => {
  // Hook para obtener nombres de compañías
  const {
    getProviderName,
    getManagerName,
    getTransporterName,
    getStorageName,
  } = useCompanyNames()

  // Check if any filters are active
  const hasActiveFilters =
    !activeStatuses.includes('all') ||
    !activeOrigins?.includes('all') ||
    !activeCategories?.includes('all') ||
    activeIsReturned !== 'all' ||
    ownerCompanyId !== '' ||
    logisticCompanyId !== '' ||
    transporterCompanyId !== '' ||
    storageCompanyId !== '' ||
    searchQuery !== ''

  const handleSearchClear = () => {
    setSearchQuery('')
  }

  const handleClearAll = () => {
    handleClearFilters()
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {/* Order Status Filter Badge */}
      {!activeStatuses.includes('all') && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>
            Estado:{' '}
            {activeStatuses
              .map(
                (s) => statusOptions.find((option) => option.value === s)?.label
              )
              .join(', ')}
          </span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={() => setActiveStatuses(['all'])}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Search Query Badge */}
      {searchQuery && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>Búsqueda: {searchQuery}</span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={handleSearchClear}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Category Filter Badge */}
      {activeCategories &&
        !activeCategories.includes('all') &&
        setActiveCategories && (
          <Badge
            className="gap-1 rounded-full py-1.5 pl-2 pr-1"
            variant="secondary"
          >
            <span>
              Categoría:{' '}
              {activeCategories
                .map(
                  (c) =>
                    categoryOptions?.find((option) => option.value === c)?.label
                )
                .join(', ')}
            </span>
            <Button
              className="ml-1 h-4 w-4"
              onClick={() => setActiveCategories(['all'])}
              size="icon"
              variant="ghost"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        )}

      {/* Returned Filter Badge */}
      {activeIsReturned &&
        activeIsReturned !== 'all' &&
        setActiveIsReturned && (
          <Badge
            className="gap-1 rounded-full py-1.5 pl-2 pr-1"
            variant="secondary"
          >
            <span>
              Retornado:{' '}
              {
                returnedOptions?.find(
                  (option) => option.value === activeIsReturned
                )?.label
              }
            </span>
            <Button
              className="ml-1 h-4 w-4"
              onClick={() => setActiveIsReturned('all')}
              size="icon"
              variant="ghost"
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        )}

      {/* Owner Company Filter Badge */}
      {ownerCompanyId && updateOwnerCompanyId && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>Proveedor: {getProviderName(ownerCompanyId)}</span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={() => updateOwnerCompanyId('')}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Logistic Company Filter Badge */}
      {logisticCompanyId && updateLogisticCompanyId && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>Operador Logístico: {getManagerName(logisticCompanyId)}</span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={() => updateLogisticCompanyId('')}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Transporter Company Filter Badge */}
      {transporterCompanyId && updateTransporterCompanyId && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>Transportista: {getTransporterName(transporterCompanyId)}</span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={() => updateTransporterCompanyId('')}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Storage Company Filter Badge */}
      {storageCompanyId && updateStorageCompanyId && (
        <Badge
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          variant="secondary"
        >
          <span>Almacenaje: {getStorageName(storageCompanyId)}</span>
          <Button
            className="ml-1 h-4 w-4"
            onClick={() => updateStorageCompanyId('')}
            size="icon"
            variant="ghost"
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )}

      {/* Legacy badges - commented out for now */}
      {/* {selectedTipo && (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>
            Tipo:{' '}
            {tipoOptions.find((option) => option.value === selectedTipo)?.label}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => setSelectedTipo('')}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )} */}

      {/* {personFilters.map((filter, index) => (
        <Badge
          key={`person-${index}`}
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>{filter.label}</span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => handleRemovePersonFilter(index)}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      ))} */}

      {/* {(selectedProvincia || selectedMunicipio) && lugarLabel && (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>{lugarLabel}</span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => {
              setSelectedProvincia('')
              setSelectedMunicipio('')
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )} */}

      {/* {dateRange.from && (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>
            {dateTypeOptions.find((dt) => dt.value === selectedDateType)?.label}
            :{' '}
            {dateRange.to
              ? `${format(dateRange.from, 'd MMM', {
                  locale: es,
                })} - ${format(dateRange.to, 'd MMM', { locale: es })}`
              : format(dateRange.from, 'd MMM yyyy', { locale: es })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => setDateRange({ from: undefined, to: undefined })}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )} */}

      {/* {(amountRange.min || amountRange.max) && (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>
            Importe: {amountRange.min || '0'} - {amountRange.max || '∞'}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => setAmountRange({ min: '', max: '' })}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )} */}

      {/* {selectedFilterCriteria.length > 0 && (
        <Badge
          variant="secondary"
          className="gap-1 rounded-full py-1.5 pl-2 pr-1"
        >
          <span>Criterios: {selectedFilterCriteria.length}</span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-1 h-4 w-4"
            onClick={() => setSelectedFilterCriteria([])}
          >
            <X className="h-3 w-3" />
          </Button>
        </Badge>
      )} */}

      {/* {activeFilters.length > 0 &&
        activeFilters.map((filter, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="gap-1 rounded-full py-1.5 pl-2 pr-1"
          >
            <span>
              {filter.name}: {filter.value}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-1 h-4 w-4"
              onClick={() => {
                setActiveFilters(activeFilters.filter((_, i) => i !== index))
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))} */}

      {/* Clear All Filters Button */}
      {hasActiveFilters && (
        <Button
          className="h-7 gap-1 text-xs"
          onClick={handleClearAll}
          size="sm"
          variant="ghost"
        >
          <FilterX className="h-3.5 w-3.5" />
          Limpiar filtros
        </Button>
      )}
    </div>
  )
}
